<template>
  <div class="magic-link-form">
    <div class="intro-section__headline">
      <h4 class="intro-section__headline__txt" v-if="step==='email-input'">{{
          $t('forms.magic_link.sign_in')
        }}</h4>
      <div v-else>
        <Icon name="grMail05" width="26"/>
        <h4 class="intro-section__headline__txt big">{{ $t('forms.magic_link.check_email_title') }}</h4>
      </div>
    </div>

    <!-- Step 1: Email Input -->
    <div v-if="step === 'email-input'" class="step-email intro-section__form">
      <div class="form-alert-errors" v-if="serverErrors && serverErrors.length > 0">
        <ul class="list-unstyled m-0">
          <li v-for="(error, i) in serverErrors" :key="i">{{ error }}</li>
        </ul>
      </div>

      <form @submit.prevent="handleEmailSubmit">
        <b-form-group :label="$t('forms.magic_link.email_placeholder')">
          <b-input-group>
            <b-input-group-prepend is-text :class="{'disabled': showPrivacyCheckboxes}">
              <Icon name="mail01" width="20"/>
            </b-input-group-prepend>
            <b-form-input
                id="email"
                type="email"
                required
                class="account-step__email"
                v-model="email"
                :placeholder="$t('forms.magic_link.email_placeholder')"
                :disabled="showPrivacyCheckboxes || isEmailReadonly"
                :readonly="isEmailReadonly"
            />
            <b-input-group-append is-text v-if="showPrivacyCheckboxes && !isEmailReadonly" class="cursor-pointer" @click="goBackToEmail">
                <Icon name="pencil" width="20"/>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Privacy checkboxes (shown for new users after email check) -->
        <div v-if="showPrivacyCheckboxes" class="form-check-container my-2">
          <div class="form-check" v-if="getAppConfigReady">
            <div v-if="$store.state.appConfig.privacyCheckboxes && getPrivacyCheckboxes.length">
              <template v-for="checkbox in getPrivacyCheckboxes">
                <b-form-checkbox
                    v-model="userCheckboxChoices[checkbox.field_name]"
                    :required="checkbox.is_required"
                    :name="checkbox.field_name"
                    :value="true"
                    :key="checkbox.field_name"
                    :unchecked-value="false"
                    :disabled="sending"
                >
                  <span v-html="checkbox.label"></span>
                </b-form-checkbox>
              </template>
            </div>
            <div v-else>
              <b-form-checkbox
                  v-model="consent_privacy_and_terms"
                  required
                  name="consent_privacy_and_terms"
                  value="true"
                  unchecked-value="false"
                  :disabled="sending"
              >
                <span v-html="privacyStatement"></span>
              </b-form-checkbox>
            </div>

            <span class="form-group--error__message" v-if="hasValidationError('consent_privacy_and_terms')">
              {{ validationErrors.consent_privacy_and_terms }}
            </span>
          </div>
        </div>

        <div class="form-group m-0">
          <button
              type="submit"
              class="btn btn-primary m-0 w-100"
              :disabled="sending"
          >
            <span v-if="!sending">{{
                showPrivacyCheckboxes ? $t('forms.magic_link.send_link') : $t('forms.magic_link.continue_btn')
              }}</span>
            <span v-else>{{ $t('forms.magic_link.checking') }}</span>
          </button>

          <div class="account-step__info mt-3">
            <div class="in d-flex align-items-center">
              <div class="mr-2">
                <Icon name="star06" width="24"/>
              </div>
              <div class="text-left small">
                {{ $t('forms.magic_link.instructions') }}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 2: Check Email Confirmation -->
    <div v-if="step === 'email-sent'" class="text-center">
      <p class="text-secondary magic-link-info" v-html="$t('forms.magic_link.check_email_description', {email})"></p>

      <div class="text-center mt-4">
        <!-- Resend -->
        <p class="text-secondary small my-3" v-if="canResend && !resending">
          {{ $t('forms.magic_link.havent_received') }}
          <a href="#" style="text-decoration: underline" @click.prevent="resendMagicLink">{{ $t('forms.magic_link.resend_link') }}</a>
        </p>
        <p class="text-secondary small my-3" v-else>
          {{$t('forms.magic_link.resend_wait', {seconds: resendCountdown})}}
        </p>
        <!-- Change email (hidden when email is locked from external source) -->
        <div class="mt-4" v-if="!isEmailLocked">
          <button
              @click="goBackToEmail"
              class="btn btn-link btn-sm"
          >
            <u>{{ $t('forms.magic_link.change_email') }}</u>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'

export default {
  name: 'MagicLinkForm',

  data() {
    return {
      step: 'email-input', // 'email-input' | 'email-sent'
      email: '',
      isNewUser: false,
      showPrivacyCheckboxes: false,
      consent_privacy_and_terms: false,
      userCheckboxChoices: {},
      validationErrors: {},
      sending: false,
      resending: false,
      serverErrors: [],
      resendCountdown: 0,
      resendTimer: null
    }
  },

  computed: {
    ...mapGetters(['thirdLevelDomain']),
    ...mapGetters('appConfig', ['getPrivacyCheckboxes', 'getAppConfigReady', 'getEventOwnerId']),
    ...mapGetters('auth', ['prefilledEmail']),

    /**
     * Whether email is locked from external source (preview JWT with forced email)
     * For magic link retry, email is prefilled but not locked
     */
    isEmailLocked() {
      // Email is only locked when it comes from preview JWT (has picacode context)
      return !!this.prefilledEmail && !!this.$store.state.auth.contextParams.picacode
    },

    /**
     * Whether email field should be readonly (when pre-filled and locked)
     */
    isEmailReadonly() {
      return this.isEmailLocked && this.step === 'email-input'
    },

    canResend() {
      return this.resendCountdown === 0
    },

    privacyStatement() {
      return this.$t('forms.signup_form.privacy_statement', {
        event_owner: this.thirdLevelDomain,
        policy_url: this.policyUrl,
        terms_url: this.termsUrl
      })
    },

    policyUrl() {
      return `https://policy.getpica.com/${this.thirdLevelDomain}/privacy`
    },

    termsUrl() {
      return `https://policy.getpica.com/${this.thirdLevelDomain}/terms`
    }
  },

  mounted() {
    // Pre-fill email if available from external source (e.g., preview API)
    if (this.prefilledEmail) {
      this.email = this.prefilledEmail
    }
  },

  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }

    // Clear prefilled email when leaving component
    if (this.prefilledEmail) {
      this.$store.commit('auth/CLEAR_CONTEXT_PARAM', 'email')
    }
  },

  watch: {
    step(newStep) {
      // Emit step change so parent can hide/show elements
      this.$emit('step-change', newStep)
    },

    getAppConfigReady: {
      handler() {
        this.setUserCheckboxChoices()
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('auth', ['requestMagicLink', 'registerUserMagicLink']),

    setUserCheckboxChoices() {
      if (this.getPrivacyCheckboxes && this.getPrivacyCheckboxes.length) {
        const choices = {}
        this.getPrivacyCheckboxes.forEach(checkbox => {
          choices[checkbox.field_name] = false
        })
        this.userCheckboxChoices = choices
      }
    },

    hasValidationError(field) {
      return this.validationErrors && this.validationErrors[field]
    },

    async handleEmailSubmit() {
      this.serverErrors = []
      this.validationErrors = {}

      // hide site banner if visible
      document.body.classList.add('site-banner--hidden')
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 400)

      // If this is a new user and privacy checkboxes are shown, register the user
      if (this.isNewUser && this.showPrivacyCheckboxes) {
        await this.registerNewUser()
        return
      }

      // Otherwise, check if user exists
      this.sending = true

      try {
        const response = await this.requestMagicLink({email: this.email})

        // Backend returns: { status: "magic_link_sent"|"not_found", email: "..." }
        if (response.status === 'not_found') {
          // New user - show privacy checkboxes
          this.isNewUser = true
          this.showPrivacyCheckboxes = true
        } else if (response.status === 'magic_link_sent') {
          // Existing user - magic link sent, show confirmation
          this.isNewUser = false
          this.step = 'email-sent'
          //this.startResendCountdown()
        }
      } catch (error) {
        console.error('Failed to request magic link:', error)
        this.serverErrors = (error.response && error.response.data && error.response.data.non_field_errors) ||
            (error.response && error.response.data && error.response.data.email) ||
            [this.$t('forms.magic_link.error_send')]
      } finally {
        this.sending = false
      }
    },

    async registerNewUser() {
      this.sending = true

      try {
        // Build payload with privacy checkboxes
        let payload = {
          email: this.email,
          language: this.$i18n.locale || 'it',
          first_name: '',
          last_name: ''
        }

        // Add checkbox consents
        if (this.getEventOwnerId && this.getPrivacyCheckboxes.length) {
          payload = {...payload, ...this.userCheckboxChoices}
        } else {
          payload.consent_privacy_and_terms = this.consent_privacy_and_terms
        }

        const response = await this.registerUserMagicLink(payload)

        // Backend returns: { status: "registration_magic_link_sent", email: "..." }
        if (response.status === 'registration_magic_link_sent') {
          this.step = 'email-sent'
          this.showPrivacyCheckboxes = false
          //this.startResendCountdown()
        }
      } catch (error) {
        console.error('Failed to register user:', error)

        // Handle validation errors
        if (error.response && error.response.data) {
          this.validationErrors = error.response.data
          this.serverErrors = error.response.data.non_field_errors ||
              error.response.data.email ||
              [this.$t('forms.magic_link.error_register')]
        } else {
          this.serverErrors = [this.$t('forms.magic_link.error_register')]
        }
      } finally {
        this.sending = false
      }
    },

    async resendMagicLink() {
      if (!this.canResend) return

      this.startResendCountdown()
      this.resending = true

      try {
        await this.requestMagicLink({email: this.email})
        //this.startResendCountdown()
      } catch (error) {
        console.error('Failed to resend magic link:', error)
        this.serverErrors = [this.$t('forms.magic_link.error_resend')]
      } finally {
        this.resending = false
      }
    },

    goBackToEmail() {
      this.step = 'email-input'
      this.serverErrors = []
      this.isNewUser = false
      this.email = ''
      this.showPrivacyCheckboxes = false
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }
    },

    startResendCountdown() {
      this.resendCountdown = 30

      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }

      this.resendTimer = setInterval(() => {
        this.resendCountdown--
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    }
  }
}
</script>
